// ** React Imports
import { useState, useEffect, useRef } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Snackbar from '@mui/material/Snackbar'
import Fade from '@mui/material/Fade'
import { Alert, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { styled, alpha, keyframes } from '@mui/material/styles'
import LinearProgress from '@mui/material/LinearProgress'

// ** Custom Imports
import CustomHeader from 'src/@core/components/Header'
import Icon from 'src/@core/components/icon'
import {
  gardenBeastSkins,
  gardenDecorationSets,
  gardenDecorations,
  gardenEvents,
  gardenIllusions,
  gardenStatues
} from 'src/data/garden'
import TablaReferencia from 'src/@core/components/table/TablaReferencia'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'

/*
  Estado compartible. plannerLevels lo gestiona GardenPlannerPanel y es, por
  construccion, solo lo que el jugador elige: el nivel al que quiere cada pieza. No
  lleva nada derivado, asi que viaja tal cual.
*/
const aEstadoMinimo = niveles => niveles ?? null

const desdeEstadoMinimo = minimo => (minimo && typeof minimo === 'object' ? minimo : null)

/*
  Tabla de referencia renderizada en el servidor. /garden/ servia 864 palabras y
  cero filas sobre 52 KB de datos: las 58 piezas viven detras de pestañas y
  selectores, asi que ninguna llegaba al HTML.

  Se juntan las cinco familias en una sola tabla porque comparten forma —nombre,
  nivel maximo y que stats dan— y separarlas obligaria al lector a mirar en cinco
  sitios para comparar dos piezas.
*/
const FAMILIAS_JARDIN = [
  ['Decoration', gardenDecorations],
  ['Statue', gardenStatues],
  ['Set', gardenDecorationSets],
  ['Beast skin', gardenBeastSkins],
  ['Illusion', gardenIllusions]
]

const COLUMNAS_JARDIN = [
  { clave: 'nombre', encabezado: 'Item' },
  { clave: 'familia', encabezado: 'Category' },
  { clave: 'tipo', encabezado: 'Type' },
  { clave: 'maxLevel', encabezado: 'Max level', numerica: true },
  { clave: 'stats', encabezado: 'Stats it gives' },
  { clave: 'topes', encabezado: 'Values at max level' }
]

const FILAS_JARDIN = FAMILIAS_JARDIN.flatMap(([familia, lista]) =>
  (lista ?? []).map(x => {
    const ultimo = Array.isArray(x.levels) && x.levels.length ? x.levels[x.levels.length - 1] : null

    const valores = Array.isArray(ultimo)
      ? ultimo
          .slice(1)
          .filter(v => v !== null && v !== undefined && v !== '')
          .join(' · ')
      : ''

    return {
      id: familia + '-' + x.key,
      nombre: x.name,
      familia,
      tipo: x.type ?? (x.items ? x.items.length + ' pieces' : '—'),
      maxLevel: x.maxLevel ?? 0,
      stats: (x.statNames ?? []).filter(Boolean).join(' · '),
      topes: valores
    }
  })
)


// Subtle pulse animation for value changes
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.04); }
  100% { transform: scale(1); }
`

// Shared data-cell typography: tabular numbers for aligned columns
const dataCell = {
  fontVariantNumeric: 'tabular-nums',
  fontSize: '0.8125rem',
  letterSpacing: '-0.01em'
}

// Table header style: lightweight uppercase labels
const thStyle = {
  fontWeight: 600,
  fontSize: '0.6875rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'text.secondary',
  borderBottom: '2px solid',
  borderBottomColor: 'divider',
  py: 1.5
}

// Alternating row style with hover
const rowSx = idx => ({
  '&:last-child td': { borderBottom: 0 },
  ...(idx % 2 === 0 ? {} : { backgroundColor: theme => alpha(theme.palette.text.primary, 0.02) }),
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme => alpha(theme.palette.primary.main, 0.04)
  }
})

const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.9)'
  }
}))

const TabPanel = ({ children, value, index }) => (
  <Box role='tabpanel' hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && (
      <Fade in timeout={350}>
        <Box
          sx={{
            '@keyframes slideUp': {
              from: { opacity: 0, transform: 'translateY(8px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            },
            animation: 'slideUp 0.35s ease-out'
          }}
        >
          {children}
        </Box>
      </Fade>
    )}
  </Box>
)

const StatTable = ({ item }) => {
  if (!item) return null

  const statNames = item.statNames || []
  const levels = item.levels || []

  // For premium decorations, the first stat column is a skill — move it to the end
  const isPremium = item.type === 'premium' && statNames.length > 1
  const displayStatNames = isPremium ? [...statNames.slice(1), skillNameMap[item.key] || 'Skill Effect'] : statNames

  return (
    <TableContainer sx={{ maxHeight: 500 }}>
      <Table size='small' stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...thStyle, minWidth: 50 }}>Lvl</TableCell>
            {displayStatNames.map((name, i) => (
              <TableCell key={i} sx={{ ...thStyle, minWidth: 90 }}>
                {name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {levels.map((row, idx) => {
            // Reorder values: for premium, move stat values first, then skill level at end
            const rawVals = row.slice(1)
            const displayVals = isPremium ? [...rawVals.slice(1), row[0]] : rawVals
            const orderedStatNames = isPremium ? [...statNames.slice(1), statNames[0]] : statNames

            return (
              <TableRow key={idx} sx={rowSx(idx)}>
                <TableCell sx={dataCell}>{row[0]}</TableCell>
                {displayVals.map((val, i) => {
                  const isSkillCol = isPremium && i === displayVals.length - 1
                  const isStatueSkill = statueSkillNames.has(orderedStatNames[i])

                  const isRaw =
                    orderedStatNames[i] === 'Fragments' ||
                    orderedStatNames[i] === 'Cross-Realm Army Size Boost' ||
                    orderedStatNames[i] === 'Max wounded' ||
                    isSkillCol ||
                    isStatueSkill

                  return (
                    <TableCell key={i} sx={dataCell}>
                      {val === null ? (
                        <Typography variant='caption' color='text.secondary'>
                          —
                        </Typography>
                      ) : isSkillCol ? (
                        val
                      ) : isStatueSkill ? (
                        row[0] - 20
                      ) : isRaw ? (
                        val
                      ) : (
                        `${val}%`
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// Empty state component
const EmptyState = ({ icon, message }) => (
  <Box
    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1.5 }}
  >
    <Icon icon={icon} style={{ fontSize: 32, opacity: 0.25 }} />
    <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', maxWidth: 240 }}>
      {message}
    </Typography>
  </Box>
)

const EventChips = ({ name }) => {
  const sources = gardenEvents[name] || []
  if (sources.length === 0)
    return (
      <Typography variant='caption' color='text.secondary'>
        Unknown
      </Typography>
    )

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {sources.map((source, i) => (
        <Chip key={i} label={source} size='small' variant='outlined' />
      ))}
    </Box>
  )
}

const typeColors = {
  premium: { border: '#f0a500', bg: 'rgba(240,165,0,0.04)' },
  normal: { border: '#5c6bc0', bg: 'rgba(92,107,192,0.04)' },
  simple: { border: '#78909c', bg: 'rgba(120,144,156,0.04)' },
  god_of_war: { border: '#f0a500', bg: 'rgba(240,165,0,0.04)' },
  monster: { border: '#e53935', bg: 'rgba(229,57,53,0.04)' }
}

const DecorationsPanel = () => {
  const [selected, setSelected] = useState(0)
  const item = gardenDecorations[selected]
  const colors = item ? typeColors[item.type] || typeColors.simple : typeColors.simple

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            borderLeft: `3px solid ${colors.border}`,
            boxShadow: 'none',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Select Decoration
          </Typography>
          <TextField select fullWidth size='small' value={selected} onChange={e => setSelected(Number(e.target.value))}>
            slotProps={{ htmlInput: { 'aria-label': 'Select Decoration' } }}
            {gardenDecorations.map((d, i) => (
              <MenuItem
                key={d.key}
                value={i}
                sx={{
                  borderLeft: `4px solid ${typeColors[d.type]?.border || '#ccc'}`,
                  backgroundColor: typeColors[d.type]?.bg || 'transparent',
                  pl: 2,
                  mb: 0.25,
                  gap: 1,
                  '&.Mui-selected': { backgroundColor: `${typeColors[d.type]?.bg || '#f5f5f5'} !important` }
                }}
              >
                {d.name}
                <Chip
                  label={d.type === 'premium' ? 'Premium' : d.type === 'normal' ? 'Normal' : 'Basic'}
                  size='small'
                  color={d.type === 'premium' ? 'warning' : d.type === 'normal' ? 'primary' : 'default'}
                  sx={{ ml: 1 }}
                />
              </MenuItem>
            ))}
          </TextField>

          {item && (
            <Fade in key={item.key} timeout={250}>
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                  Max Level: {item.maxLevel}
                </Typography>
                <Typography variant='body2' sx={{ mt: 1, fontWeight: 'bold' }}>
                  Stats:
                </Typography>
                {item.statNames.filter(Boolean).map((stat, i) => (
                  <Typography key={i} variant='caption' display='block' sx={{ ml: 1 }}>
                    • {stat}
                  </Typography>
                ))}
                <Typography variant='body2' sx={{ mt: 1.5, fontWeight: 'bold' }}>
                  Event:
                </Typography>
                <EventChips name={item.name} />
              </Box>
            </Fade>
          )}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 2.5,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {item?.name} — Level Progression
          </Typography>
          <StatTable item={item} />
        </Card>
      </Grid>
    </Grid>
  )
}

const DecorationSetsPanel = () => {
  const [selected, setSelected] = useState(0)
  const item = gardenDecorationSets[selected]

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            borderLeft: '3px solid #f0a500',
            boxShadow: 'none',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Select Set
          </Typography>
          <TextField select fullWidth size='small' value={selected} onChange={e => setSelected(Number(e.target.value))}>
            slotProps={{ htmlInput: { 'aria-label': 'Select Set' } }}
            {gardenDecorationSets.map((s, i) => (
              <MenuItem
                key={s.key}
                value={i}
                sx={{
                  borderLeft: '4px solid #f0a500',
                  backgroundColor: '#fff8e1',
                  pl: 2,
                  mb: 0.25,
                  '&.Mui-selected': { backgroundColor: '#fff8e1 !important' }
                }}
              >
                {s.name}
              </MenuItem>
            ))}
          </TextField>

          {item && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                Items ({item.items.length}):
              </Typography>
              {item.items.map((m, i) => (
                <Typography key={i} variant='caption' display='block' sx={{ ml: 1 }}>
                  • {m}
                </Typography>
              ))}
              <Typography variant='body2' sx={{ mt: 1, fontWeight: 'bold' }}>
                Max Level: {item.maxLevel}
              </Typography>
              <Typography variant='body2' sx={{ mt: 1, fontWeight: 'bold' }}>
                Stats:
              </Typography>
              {item.statNames.map((stat, i) => (
                <Typography key={i} variant='caption' display='block' sx={{ ml: 1 }}>
                  • {stat}
                </Typography>
              ))}
              <Typography variant='body2' sx={{ mt: 1.5, fontWeight: 'bold' }}>
                Event:
              </Typography>
              <EventChips name={item.name} />
            </Box>
          )}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 2.5,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {item?.name} — Level Progression
          </Typography>
          <StatTable item={item} />
        </Card>
      </Grid>
    </Grid>
  )
}

const StatuesPanel = () => {
  const [selected, setSelected] = useState(0)
  const item = gardenStatues[selected]
  const colors = item ? typeColors[item.type] || typeColors.simple : typeColors.simple

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            borderLeft: `3px solid ${colors.border}`,
            boxShadow: 'none',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Select Statue
          </Typography>
          <TextField select fullWidth size='small' value={selected} onChange={e => setSelected(Number(e.target.value))}>
            slotProps={{ htmlInput: { 'aria-label': 'Select Statue' } }}
            {gardenStatues.map((s, i) => (
              <MenuItem
                key={s.key}
                value={i}
                sx={{
                  borderLeft: `4px solid ${typeColors[s.type]?.border || '#ccc'}`,
                  backgroundColor: typeColors[s.type]?.bg || 'transparent',
                  pl: 2,
                  mb: 0.25,
                  gap: 1,
                  '&.Mui-selected': { backgroundColor: `${typeColors[s.type]?.bg || '#f5f5f5'} !important` }
                }}
              >
                {s.name}
                <Chip
                  label={s.type === 'god_of_war' ? 'God of War' : 'Monster'}
                  size='small'
                  color={s.type === 'god_of_war' ? 'warning' : 'error'}
                  sx={{ ml: 1 }}
                />
              </MenuItem>
            ))}
          </TextField>

          {item && (
            <Fade in key={item.key} timeout={250}>
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                  Max Level: {item.maxLevel}
                </Typography>
                <Typography variant='body2' sx={{ mt: 1.5, fontWeight: 'bold' }}>
                  Event:
                </Typography>
                <EventChips name={item.name} />
              </Box>
            </Fade>
          )}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 2.5,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {item?.name} — Level Progression
          </Typography>
          <StatTable item={item} />
        </Card>
      </Grid>
    </Grid>
  )
}

// Build reverse mapping: event → items
const eventToItems = (() => {
  const map = {}
  Object.entries(gardenEvents).forEach(([itemName, events]) => {
    events.forEach(evt => {
      if (!map[evt]) map[evt] = []
      map[evt].push(itemName)
    })
  })

  return map
})()

const allUniqueEvents = Object.keys(eventToItems).sort()

const EventLookupPanel = () => {
  const [selected, setSelected] = useState('')

  const items = selected ? eventToItems[selected] || [] : []

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            boxShadow: 'none',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Select Event
          </Typography>
          <TextField select fullWidth size='small' value={selected} onChange={e => setSelected(e.target.value)}>
            slotProps={{ htmlInput: { 'aria-label': 'Select Event' } }}
            {allUniqueEvents.map(evt => (
              <MenuItem key={evt} value={evt}>
                {evt}
              </MenuItem>
            ))}
          </TextField>

          {selected && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                Items available: {items.length}
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 2.5,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
            minHeight: 200
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
            {selected ? `Items from: ${selected}` : 'Select an event to see available items'}
          </Typography>
          {!selected && (
            <EmptyState
              icon='mdi:calendar-search'
              message='Choose an event from the left to see which items are available'
            />
          )}
          {selected && items.length === 0 && (
            <EmptyState icon='mdi:package-variant' message='No items found for this event' />
          )}
          {items.length > 0 && (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Item</TableCell>
                    <TableCell sx={thStyle}>Type</TableCell>
                    <TableCell sx={thStyle}>Stats</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(name => {
                    const nameLower = name.toLowerCase()
                    const deco = gardenDecorations.find(d => d.name.toLowerCase() === nameLower)
                    const set = gardenDecorationSets.find(s => s.name.toLowerCase() === nameLower)
                    const statue = gardenStatues.find(s => s.name.toLowerCase() === nameLower)
                    const entry = deco || set || statue

                    const type = deco
                      ? `Decoration (${deco.type})`
                      : set
                        ? 'Decoration Set'
                        : statue
                          ? `Statue (${statue.type === 'god_of_war' ? 'God of War' : 'Monster'})`
                          : null

                    const stats = entry?.statNames?.filter(s => s !== 'Fragments').join(', ') || null

                    return (
                      <TableRow key={name} hover>
                        <TableCell>{entry ? entry.name : name}</TableCell>
                        <TableCell>
                          {type ? (
                            <Chip
                              label={type}
                              size='small'
                              variant='outlined'
                              color={
                                deco
                                  ? deco.type === 'premium'
                                    ? 'warning'
                                    : deco.type === 'normal'
                                      ? 'primary'
                                      : 'default'
                                  : set
                                    ? 'warning'
                                    : statue
                                      ? statue.type === 'god_of_war'
                                        ? 'warning'
                                        : 'error'
                                      : 'default'
                              }
                            />
                          ) : (
                            <Typography variant='caption' color='text.secondary'>
                              No data yet
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {stats ? (
                            <Typography variant='caption'>{stats}</Typography>
                          ) : (
                            <Typography variant='caption' color='text.secondary'>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Grid>
    </Grid>
  )
}

// Collect all stat names across all items
// Normalize stat names that are the same thing spelled differently
const statNameMap = {
  'Archer and mage attack': 'Increase the Attack of Archers and Mages',
  'Archers and Mages Attack': 'Increase the Attack of Archers and Mages',
  'Archers and mages attack': 'Increase the Attack of Archers and Mages',
  'Cross realm dmg taken reduced': 'Cross-Realm Troops Damage Taken Reduction',
  'Cross realm dmg taken reduction': 'Cross-Realm Troops Damage Taken Reduction',
  'Cross realm dmg': 'Cross-Realm Troops Damage',
  'Cross realm troops HP': 'Cross-Realm Troops HP',
  'Cross-Realm Army Size Boost': 'Cross-Realm Army Size Boost',
  'Cross-Realm Attack Boost': 'Cross-Realm Attack Boost',
  'Archers and Mages Damage Dealt Boost': 'Archers and Mages Damage Dealt Boost',
  'Hp boost': 'HP Boost',
  'HP boost': 'HP Boost',
  '5% HP boost': 'HP Boost',
  'Attack boost': 'Attack Boost',
  'Enemy tooprs attack reduction': 'Enemy Troops Attack Reduction',
  'Enemy troops attack reduction': 'Enemy Troops Attack Reduction',
  'Enemy troops hp reduction': 'Enemy Troops HP Reduction',
  'Enemy troops HP reduction': 'Enemy Troops HP Reduction',
  'Infantry and cavalry HP': 'Infantry and Cavalry HP Boost',
  'Infantry and Cavalry HP Boost': 'Infantry and Cavalry HP Boost',
  'Infantry and cavalry HP boost': 'Infantry and Cavalry HP Boost',
  'Angel HP': 'Angel HP',
  'Angel Damage': 'Angel Damage',
  'Angel damage': 'Angel Damage',
  'Angel attack': 'Angel Attack',
  'Max wounded': 'Max Wounded',
  'Infantry and cavalry damage taken reduction': 'Infantry and Cavalry Damage Taken Reduction',
  'Infantry and Cavalry Damage Taken Reduction': 'Infantry and Cavalry Damage Taken Reduction',
  'Load boost': 'Load Boost',
  'Gather speed boost': 'Gather Speed Boost'
}

// Stats that are raw numbers (not percentages)
const rawNumberStats = new Set(['Max Wounded', 'Cross-Realm Army Size Boost'])

// Skill name mapping: premium decoration key → actual in-game skill name
const skillNameMap = {
  mirage: 'Mirage Suppression',
  poseidon_warship: "Poseidon's Domain",
  candy_castle: 'Fairytale Blessing',
  countryside_party: 'Morale Deterrence',
  love_in_the_city: "Lover's Blessing",
  star_collection: 'Tenacious',
  leviathan_magic_bottle: "Deep Sea's Rage",
  christmas_tale: 'Focus',
  dragon_guard: 'Surmount',
  phantom_playground: 'Ghost Phantom',
  castle_in_the_sky: 'Stand Fast'
}

// Statue skill names that should be treated as skills (not summed percentage stats)
const statueSkillNames = new Set(['Divine Eye', 'Fury Force', 'Pallas Armor'])

const normalizeStat = name => statNameMap[name] || name

const allStatNames = (() => {
  const stats = new Set()
  gardenDecorations.forEach(d =>
    d.statNames.forEach(s => {
      if (s !== 'Fragments') stats.add(normalizeStat(s))
    })
  )
  gardenDecorationSets.forEach(s => s.statNames.forEach(n => stats.add(normalizeStat(n))))
  gardenStatues.forEach(s =>
    s.statNames.forEach(n => {
      if (n !== 'Fragments') stats.add(normalizeStat(n))
    })
  )

  return [...stats].sort()
})()

const StatFilterPanel = () => {
  const [selectedStat, setSelectedStat] = useState('')

  const matchingItems = selectedStat
    ? [
        ...gardenDecorations
          .filter(d => d.statNames.some(s => normalizeStat(s) === selectedStat))
          .map(d => ({ ...d, category: 'Decoration' })),
        ...gardenDecorationSets
          .filter(s => s.statNames.some(n => normalizeStat(n) === selectedStat))
          .map(s => ({ ...s, category: 'Set' })),
        ...gardenStatues
          .filter(s => s.statNames.some(n => normalizeStat(n) === selectedStat))
          .map(s => ({ ...s, category: 'Statue' }))
      ]
    : []

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            boxShadow: 'none',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Filter by Stat
          </Typography>
          <TextField select fullWidth size='small' value={selectedStat} onChange={e => setSelectedStat(e.target.value)}>
            slotProps={{ htmlInput: { 'aria-label': 'Filter by Stat' } }}
            {allStatNames.map(stat => (
              <MenuItem key={stat} value={stat}>
                {stat}
              </MenuItem>
            ))}
          </TextField>

          {selectedStat && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                Items with this stat: {matchingItems.length}
              </Typography>
            </Box>
          )}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 2.5,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
            minHeight: 200
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
            {selectedStat ? `Items with: ${selectedStat}` : 'Select a stat to filter items'}
          </Typography>
          {!selectedStat && (
            <EmptyState
              icon='mdi:chart-box-outline'
              message='Choose a stat from the left to see which items provide it'
            />
          )}
          {selectedStat && matchingItems.length === 0 && (
            <EmptyState icon='mdi:magnify-close' message='No items found with this stat' />
          )}
          {matchingItems.length > 0 && (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Item</TableCell>
                    <TableCell sx={thStyle}>Category</TableCell>
                    <TableCell sx={thStyle}>Max Level</TableCell>
                    <TableCell sx={thStyle}>Max Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchingItems.map(item => {
                    const statIdx = item.statNames.findIndex(s => normalizeStat(s) === selectedStat)
                    const lastLevel = item.levels[item.levels.length - 1]
                    const maxVal = lastLevel ? lastLevel[statIdx + 1] : '—'

                    return (
                      <TableRow key={item.key} hover>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.category}
                            size='small'
                            variant='outlined'
                            color={
                              item.category === 'Decoration'
                                ? item.type === 'premium'
                                  ? 'warning'
                                  : item.type === 'normal'
                                    ? 'primary'
                                    : 'default'
                                : item.category === 'Set'
                                  ? 'warning'
                                  : item.type === 'god_of_war'
                                    ? 'warning'
                                    : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>{item.maxLevel}</TableCell>
                        <TableCell>
                          {maxVal !== null ? `${maxVal}${rawNumberStats.has(selectedStat) ? '' : '%'}` : '—'}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Grid>
    </Grid>
  )
}

const GardenPlannerPanel = ({ savedLevels, onLevelsChange }) => {
  const [levels, setLevels] = useState(() => {
    const initial = {}
    gardenDecorations.forEach(d => {
      initial[d.key] = savedLevels?.[d.key] || 0
    })
    gardenDecorationSets.forEach(s => {
      s.items.forEach((item, i) => {
        const itemKey = `${s.key}_item_${i}`
        initial[itemKey] = savedLevels?.[itemKey] || 0
      })
    })
    gardenStatues.forEach(s => {
      initial[s.key] = savedLevels?.[s.key] || 0
    })

    return initial
  })

  const [searchFilter, setSearchFilter] = useState('')

  // Sync when savedLevels changes (e.g. reset)
  useEffect(() => {
    const updated = {}
    gardenDecorations.forEach(d => {
      updated[d.key] = savedLevels?.[d.key] || 0
    })
    gardenDecorationSets.forEach(s => {
      s.items.forEach((item, i) => {
        const itemKey = `${s.key}_item_${i}`
        updated[itemKey] = savedLevels?.[itemKey] || 0
      })
    })
    gardenStatues.forEach(s => {
      updated[s.key] = savedLevels?.[s.key] || 0
    })
    setLevels(updated)
  }, [savedLevels])

  const handleLevel = (key, val) => {
    const parsed = val === '' ? 0 : parseInt(val, 10)
    const newLevels = { ...levels, [key]: isNaN(parsed) ? 0 : parsed }
    setLevels(newLevels)
    if (onLevelsChange) onLevelsChange(newLevels)
  }

  // Calculate total stats from current levels
  const totalStats = (() => {
    const stats = {}
    const skills = []
    gardenDecorations.forEach(d => {
      const lvl = levels[d.key]
      if (lvl > 0) {
        // For premium decorations, always show the skill
        if (d.type === 'premium') {
          skills.push({ item: d.name, skillName: skillNameMap[d.key] || d.name, level: lvl })
        }
        const row = d.levels.find(r => r[0] === lvl)
        if (row) {
          d.statNames.forEach((name, i) => {
            if (name === 'Fragments') return
            // Skip first stat for premium decorations (it's the skill effect, not a summable stat)
            if (d.type === 'premium' && i === 0) return
            const val = row[i + 1]
            if (val === null) return
            if (typeof val === 'number') {
              const normalized = normalizeStat(name)
              stats[normalized] = (stats[normalized] || 0) + val
            }
          })
        }
      }
    })
    gardenStatues.forEach(s => {
      const lvl = levels[s.key]
      if (lvl > 0) {
        const row = s.levels.find(r => r[0] === lvl)
        if (row) {
          s.statNames.forEach((name, i) => {
            if (name === 'Fragments') return
            if (statueSkillNames.has(name)) {
              // Statue skills unlock at level 21, so skill level = statue level - 20
              if (lvl >= 21) {
                skills.push({ skillName: name, level: lvl - 20, item: s.name })
              }

              return
            }
            const val = row[i + 1]
            if (val === null) return
            if (typeof val === 'number') {
              const normalized = normalizeStat(name)
              stats[normalized] = (stats[normalized] || 0) + val
            } else if (typeof val === 'string') {
              skills.push({ item: s.name, skill: name, value: val })
            }
          })
        }
      }
    })
    gardenDecorationSets.forEach(s => {
      // Set level = minimum of all individual item levels
      const itemLevels = s.items.map((_, i) => levels[`${s.key}_item_${i}`] || 0)
      const lvl = Math.min(...itemLevels)
      if (lvl > 0) {
        const row = s.levels.find(r => r[0] === lvl)
        if (row) {
          s.statNames.forEach((name, i) => {
            const val = row[i + 1]
            if (val === null) return
            if (typeof val === 'number') {
              const normalized = normalizeStat(name)
              stats[normalized] = (stats[normalized] || 0) + val
            } else if (typeof val === 'string') {
              skills.push({ item: s.name, skill: name, value: val })
            }
          })
        }
      }
    })

    return { stats, skills }
  })()

  // Smart upgrade advisor - accounts for stat unlocks, stat importance, and quick wins
  const upgradeAdvisor = (() => {
    // Stat importance weights based on PvP value:
    // Tier 1 (4.0): Cross-realm army size, troops damage, troops damage reduction
    // Tier 2 (3.0): Enemy reductions (attack/HP)
    // Tier 3 (2.0): Damage taken reductions
    // Tier 4 (1.0): General attack/HP stats
    const statWeights = {
      // Tier 1 - Cross-realm & troops damage/reduction (most important)
      'cross-realm army size boost': 4,
      'cross-realm troops damage': 4,
      'cross-realm troops damage taken reduction': 4,
      // Tier 2 - Enemy reductions
      'enemy troops attack reduction': 3,
      'enemy troops hp reduction': 3,
      'cross-realm attack boost': 3.5,
      'cross-realm troops hp': 3.5,
      // Tier 3 - Damage taken reductions
      'infantry and cavalry damage taken reduction': 2,
      'archers and mages damage dealt boost': 2,
      // Tier 4 - General stats (lowest)
      'attack boost': 1,
      'hp boost': 1,
      'angel hp': 1,
      'angel attack': 1,
      'angel damage': 1.5,
      'infantry and cavalry hp boost': 1,
      'increase the attack of archers and mages': 1,
      'max wounded': 0.5
    }

    const getStatWeight = name => {
      const lower = normalizeStat(name).toLowerCase()
      for (const [key, weight] of Object.entries(statWeights)) {
        if (lower.includes(key)) return weight
      }

      return 1
    }

    const suggestions = []

    // Helper to evaluate any item (statue, decoration, or set)
    const evaluateItem = (item, category) => {
      const currentLvl = levels[item.key] || 0
      if (currentLvl >= item.maxLevel) return

      const nextRow = item.levels.find(r => r[0] === currentLvl + 1)
      const currentRow = item.levels.find(r => r[0] === currentLvl)
      if (!nextRow) return

      // Determine fragment cost (statues have Fragments column, others don't)
      const fragIdx = item.statNames.indexOf('Fragments')
      const fragmentCost = fragIdx !== -1 ? nextRow[fragIdx + 1] || 0 : null
      if (fragIdx !== -1 && fragmentCost === 0) return

      // Calculate weighted stat gain and detect new unlocks
      let weightedGain = 0
      let rawGain = 0
      let unlocksNew = false
      const unlockNames = []

      item.statNames.forEach((name, i) => {
        if (name === 'Fragments') return
        const nextVal = nextRow[i + 1]
        const currVal = currentRow ? currentRow[i + 1] : null
        const weight = getStatWeight(name)

        if (typeof nextVal === 'number' && (currVal === null || currVal === 0)) {
          unlocksNew = true
          unlockNames.push(normalizeStat(name))
          weightedGain += nextVal * weight * 2
          rawGain += nextVal
        } else if (typeof nextVal === 'number' && typeof currVal === 'number') {
          const delta = nextVal - currVal
          weightedGain += delta * weight
          rawGain += delta
        }
      })

      if (rawGain <= 0 && !unlocksNew) return

      // Score calculation
      let score
      if (fragmentCost !== null) {
        // Statue: efficiency per fragment with quick-win multiplier
        const efficiency = weightedGain / fragmentCost

        const quickWinMultiplier =
          fragmentCost <= 20
            ? 1.5
            : fragmentCost <= 50
              ? 1.3
              : fragmentCost <= 100
                ? 1.1
                : fragmentCost <= 200
                  ? 0.9
                  : fragmentCost <= 400
                    ? 0.8
                    : 0.7
        score = efficiency * quickWinMultiplier * (unlocksNew ? 1.5 : 1)
      } else {
        // Decoration: pure weighted gain, premium skills get priority boost
        const premiumBoost = item.type === 'premium' ? 2 : 1
        score = weightedGain * premiumBoost * (unlocksNew ? 1.5 : 1)
      }

      // Generate human-readable reason
      const reasons = []
      if (unlocksNew) reasons.push(`Unlocks new stat${unlockNames.length > 1 ? 's' : ''}`)
      if (fragmentCost !== null) {
        if (fragmentCost <= 20) reasons.push('Very low cost — quick win')
        else if (fragmentCost <= 50) reasons.push('Low cost — easy to complete')
        else if (fragmentCost >= 400) reasons.push('High cost — slow progress')
      }

      // Best stat contributing
      let bestStatName = ''
      let bestStatWeight = 0
      item.statNames.forEach((name, i) => {
        if (name === 'Fragments') return
        const w = getStatWeight(name)
        const nextVal = nextRow[i + 1]
        const currVal = currentRow ? currentRow[i + 1] : null
        if (typeof nextVal === 'number' && w > bestStatWeight) {
          if (currVal === null || (typeof currVal === 'number' && nextVal > currVal)) {
            bestStatWeight = w
            bestStatName = normalizeStat(name)
          }
        }
      })
      if (bestStatWeight >= 3) reasons.push(`Top-tier stat: ${bestStatName}`)
      else if (bestStatWeight >= 2) reasons.push(`Good stat: ${bestStatName}`)

      if (reasons.length === 0) reasons.push('Balanced improvement')

      suggestions.push({
        name: item.name,
        key: item.key,
        category,
        currentLvl,
        nextLvl: currentLvl + 1,
        fragmentCost,
        totalGain: Math.round(rawGain * 100) / 100,
        weightedGain: Math.round(weightedGain * 100) / 100,
        score: Math.round(score * 1000) / 1000,
        unlocksNew,
        unlockNames,
        isQuickWin: fragmentCost !== null && fragmentCost <= 30,
        reasons
      })
    }

    gardenStatues.forEach(s => evaluateItem(s, 'Statue'))
    gardenDecorations.filter(d => d.type === 'premium').forEach(d => evaluateItem(d, 'Decoration'))
    // Normal/simple decorations excluded: trivial upgrades with no strategic value
    // Sets excluded: requires upgrading 5 items per level — too expensive to recommend

    return suggestions.sort((a, b) => b.score - a.score)
  })()

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 3,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiOutlinedInput-root': {
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1.5px',
                boxShadow: theme => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`
              }
            }
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 2, letterSpacing: '0.02em' }}>
            Set Your Levels
          </Typography>

          <TextField
            slotProps={{ htmlInput: { 'aria-label': 'Set Your Levels' } }}
            fullWidth
            size='small'
            placeholder='Search decorations or statues...'
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  <Icon icon='mdi:magnify' />
                </Box>
              )
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'warning.main' }} />
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
            >
              God of War Statues
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, mb: 4 }}>
            {gardenStatues
              .filter(s => s.type === 'god_of_war')
              .filter(s => s.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .map(s => (
                <TextField
                  key={s.key}
                  size='small'
                  type='number'
                  label={s.name}
                  value={levels[s.key] || ''}
                  onFocus={e => e.target.select()}
                  onChange={e => handleLevel(s.key, e.target.value)}
                  inputProps={{ min: 0, max: s.maxLevel, style: { fontVariantNumeric: 'tabular-nums' } }}
                />
              ))}
          </Box>

          <Divider sx={{ mb: 3.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'error.main' }} />
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
            >
              Monster Statues
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, mb: 4 }}>
            {gardenStatues
              .filter(s => s.type === 'monster')
              .filter(s => s.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .map(s => (
                <TextField
                  key={s.key}
                  size='small'
                  type='number'
                  label={s.name}
                  value={levels[s.key] || ''}
                  onFocus={e => e.target.select()}
                  onChange={e => handleLevel(s.key, e.target.value)}
                  inputProps={{ min: 0, max: s.maxLevel, style: { fontVariantNumeric: 'tabular-nums' } }}
                />
              ))}
          </Box>

          <Divider sx={{ mb: 3.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'warning.main' }} />
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
            >
              Premium Decorations
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, mb: 4 }}>
            {gardenDecorations
              .filter(d => d.type === 'premium')
              .filter(d => d.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .map(d => (
                <TextField
                  key={d.key}
                  size='small'
                  type='number'
                  label={d.name}
                  value={levels[d.key] || ''}
                  onFocus={e => e.target.select()}
                  onChange={e => handleLevel(d.key, e.target.value)}
                  inputProps={{ min: 0, max: d.maxLevel, style: { fontVariantNumeric: 'tabular-nums' } }}
                />
              ))}
          </Box>

          <Divider sx={{ mb: 3.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'info.main' }} />
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
            >
              Normal Decorations
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, mb: 4 }}>
            {gardenDecorations
              .filter(d => d.type === 'normal')
              .filter(d => d.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .map(d => (
                <TextField
                  key={d.key}
                  size='small'
                  type='number'
                  label={d.name}
                  value={levels[d.key] || ''}
                  onFocus={e => e.target.select()}
                  onChange={e => handleLevel(d.key, e.target.value)}
                  inputProps={{ min: 0, max: d.maxLevel, style: { fontVariantNumeric: 'tabular-nums' } }}
                />
              ))}
          </Box>

          <Divider sx={{ mb: 3.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'text.disabled' }} />
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
            >
              Simple Decorations
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 2, mb: 4 }}>
            {gardenDecorations
              .filter(d => d.type === 'simple')
              .filter(d => d.name.toLowerCase().includes(searchFilter.toLowerCase()))
              .map(d => (
                <TextField
                  key={d.key}
                  size='small'
                  type='number'
                  label={d.name}
                  value={levels[d.key] || ''}
                  onFocus={e => e.target.select()}
                  onChange={e => handleLevel(d.key, e.target.value)}
                  inputProps={{ min: 0, max: d.maxLevel, style: { fontVariantNumeric: 'tabular-nums' } }}
                />
              ))}
          </Box>

          <Divider sx={{ mb: 3.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'secondary.main' }} />
            <Typography
              variant='caption'
              sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}
            >
              Decoration Sets
            </Typography>
          </Box>
          {gardenDecorationSets
            .filter(
              s =>
                s.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
                s.items.some(item => item.toLowerCase().includes(searchFilter.toLowerCase()))
            )
            .map(s => {
              const itemLevels = s.items.map((_, i) => levels[`${s.key}_item_${i}`] || 0)
              const setLevel = Math.min(...itemLevels)

              return (
                <Box key={s.key} sx={{ mb: 2, p: 2, borderRadius: '6px', border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant='caption' sx={{ fontWeight: 600 }}>
                      {s.name}
                    </Typography>
                    <Chip
                      label={`Lv ${setLevel}`}
                      size='small'
                      color={setLevel > 0 ? 'secondary' : 'default'}
                      sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                    />
                    <Typography variant='caption' color='text.secondary'>
                      / {s.maxLevel}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
                    {s.items.map((item, i) => (
                      <TextField
                        key={`${s.key}_item_${i}`}
                        size='small'
                        type='number'
                        label={item}
                        value={levels[`${s.key}_item_${i}`] || ''}
                        onFocus={e => e.target.select()}
                        onChange={e => handleLevel(`${s.key}_item_${i}`, e.target.value)}
                        inputProps={{ min: 0, max: s.maxLevel, style: { fontVariantNumeric: 'tabular-nums' } }}
                      />
                    ))}
                  </Box>
                </Box>
              )
            })}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            mb: 3,
            borderLeft: '3px solid',
            borderLeftColor: 'success.main',
            boxShadow: 'none'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Next Best Upgrade
          </Typography>
          <Box sx={{ mb: 2, p: 1.5, borderRadius: '6px', backgroundColor: 'action.hover' }}>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', lineHeight: 1.6 }}>
              <strong>How scoring works:</strong> Upgrades are ranked by stat importance (cross-realm &gt; enemy
              reductions &gt; damage taken reduction &gt; general stats), fragment cost efficiency for statues, and
              whether new stats unlock. Focuses on statues and premium decoration skills.
            </Typography>
          </Box>
          {upgradeAdvisor.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Icon icon='mdi:trophy-outline' style={{ fontSize: 28, opacity: 0.2, marginBottom: 8 }} />
              <Typography variant='caption' display='block' color='text.secondary'>
                All items are at max level
              </Typography>
            </Box>
          ) : (
            <>
              {(() => {
                const maxScore = upgradeAdvisor[0]?.score || 1
                const rankColors = ['success.main', 'warning.main', 'info.main']

                return upgradeAdvisor.slice(0, 3).map((s, i) => (
                  <Box
                    key={s.key}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: i === 0 ? 'success.main' : 'divider',
                      backgroundColor: i === 0 ? theme => alpha(theme.palette.success.main, 0.04) : undefined
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {i < 3 ? (
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme => alpha(theme.palette[rankColors[i].split('.')[0]].main, 0.12),
                              flexShrink: 0
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.625rem',
                                fontWeight: 800,
                                color: rankColors[i],
                                lineHeight: 1
                              }}
                            >
                              {i + 1}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              color: 'text.secondary',
                              minWidth: 20,
                              textAlign: 'center'
                            }}
                          >
                            {i + 1}
                          </Typography>
                        )}
                        <Typography variant='caption' sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                          {s.name}
                        </Typography>
                        <Chip
                          label={s.category}
                          size='small'
                          variant='outlined'
                          sx={{ height: 14, fontSize: '0.55rem', fontWeight: 600 }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {s.unlocksNew && (
                          <Chip
                            label='UNLOCK'
                            size='small'
                            color='warning'
                            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }}
                          />
                        )}
                        {s.isQuickWin && (
                          <Chip
                            label='QUICK'
                            size='small'
                            color='info'
                            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }}
                          />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <LinearProgress
                        variant='determinate'
                        // una barra de progreso sin nombre no dice de que informa
                        aria-label={`${s.name ?? 'Decoration'} score`}
                        value={Math.min((s.score / maxScore) * 100, 100)}
                        sx={{
                          flex: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: theme => alpha(theme.palette.success.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 2,
                            backgroundColor: i === 0 ? 'success.main' : 'text.disabled'
                          }
                        }}
                      />
                      <Typography
                        variant='caption'
                        sx={{
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          minWidth: 36,
                          textAlign: 'right',
                          color: i === 0 ? 'customColors.successText' : 'text.secondary'
                        }}
                      >
                        {s.score}
                      </Typography>
                    </Box>
                    <Typography variant='caption' color='text.secondary' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      Lv {s.currentLvl}→{s.nextLvl}
                      {s.fragmentCost !== null ? ` · ${s.fragmentCost} frags` : ''} · +{s.totalGain}%
                    </Typography>
                    {s.unlocksNew && (
                      <Typography variant='caption' sx={{ color: 'customColors.warningText', display: 'block', mt: 0.25 }}>
                        Unlocks: {s.unlockNames.join(', ')}
                      </Typography>
                    )}
                  </Box>
                ))
              })()}
            </>
          )}
        </Card>

        <Card
          sx={{
            p: 2.5,
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            boxShadow: 'none'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 2, letterSpacing: '0.02em' }}>
            Total Stats
          </Typography>
          {Object.keys(totalStats.stats).length === 0 && totalStats.skills.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Icon icon='mdi:chart-line-variant' style={{ fontSize: 28, opacity: 0.2, marginBottom: 8 }} />
              <Typography variant='caption' display='block' color='text.secondary'>
                Set some levels to see your total stats
              </Typography>
            </Box>
          ) : (
            <>
              {Object.entries(totalStats.stats)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([stat, val]) => (
                  <Box
                    key={stat}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      py: 0.4,
                      transition: 'background-color 0.3s ease',
                      px: 0.5,
                      mx: -0.5,
                      borderRadius: '4px',
                      '&:hover': { backgroundColor: theme => alpha(theme.palette.primary.main, 0.04) }
                    }}
                  >
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                      {stat}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        fontFamily: 'monospace',
                        letterSpacing: '-0.02em',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {rawNumberStats.has(stat)
                        ? `+${Math.round(val).toLocaleString()}`
                        : `${Math.round(val * 10) / 10}%`}
                    </Typography>
                  </Box>
                ))}
              {totalStats.skills.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant='caption' sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                    Decoration Skills
                  </Typography>
                  {totalStats.skills
                    .slice()
                    .sort((a, b) => (a.skillName || a.item).localeCompare(b.skillName || b.item))
                    .map((s, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          py: 0.4,
                          px: 0.5,
                          mx: -0.5,
                          borderRadius: '4px',
                          '&:hover': { backgroundColor: theme => alpha(theme.palette.primary.main, 0.04) }
                        }}
                      >
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                          {s.skillName || s.item}
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            fontFamily: 'monospace',
                            letterSpacing: '-0.02em'
                          }}
                        >
                          Lv.{s.level}
                        </Typography>
                      </Box>
                    ))}
                </>
              )}
            </>
          )}
        </Card>
      </Grid>
    </Grid>
  )
}

const FragmentCalculatorPanel = () => {
  const [selected, setSelected] = useState(0)
  const [fromLevel, setFromLevel] = useState(0)
  const [toLevel, setToLevel] = useState(1)
  const tableRef = useRef(null)

  const item = gardenStatues[selected]

  const totalFragments = (() => {
    if (!item) return 0
    let total = 0
    const fragIdx = item.statNames.indexOf('Fragments')
    if (fragIdx === -1) return 0
    item.levels.forEach(row => {
      const lvl = row[0]
      if (lvl > fromLevel && lvl <= toLevel) {
        total += row[fragIdx + 1] || 0
      }
    })

    return total
  })()

  // Auto-scroll table to fromLevel row
  useEffect(() => {
    if (!tableRef.current || !item) return
    const row = tableRef.current.querySelector(`[data-level="${fromLevel + 1}"]`)
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [fromLevel, item])

  return (
    <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card
          sx={{
            p: 2.5,
            borderLeft: '3px solid',
            borderLeftColor: 'success.main',
            boxShadow: 'none',
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, letterSpacing: '0.02em' }}>
            Fragment Calculator
          </Typography>
          <TextField
            slotProps={{ htmlInput: { 'aria-label': 'Fragment Calculator' } }}
            select
            fullWidth
            size='small'
            value={selected}
            onChange={e => {
              setSelected(Number(e.target.value))
              setFromLevel(0)
              setToLevel(1)
            }}
            sx={{ mb: 2 }}
          >
            {gardenStatues.map((s, i) => (
              <MenuItem
                key={s.key}
                value={i}
                sx={{
                  borderLeft: `4px solid ${typeColors[s.type]?.border || '#ccc'}`,
                  backgroundColor: typeColors[s.type]?.bg || 'transparent',
                  pl: 2,
                  mb: 0.25,
                  gap: 1,
                  '&.Mui-selected': { backgroundColor: `${typeColors[s.type]?.bg || '#f5f5f5'} !important` }
                }}
              >
                {s.name}
                <Chip
                  label={s.type === 'god_of_war' ? 'God of War' : 'Monster'}
                  size='small'
                  color={s.type === 'god_of_war' ? 'warning' : 'error'}
                  sx={{ ml: 1 }}
                />
              </MenuItem>
            ))}
          </TextField>

          {item && (
            <>
              <TextField
                fullWidth
                size='small'
                type='number'
                label='From Level'
                value={fromLevel}
                onFocus={e => e.target.select()}
                onChange={e => {
                  const parsed = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                  const val = Math.max(0, Math.min(isNaN(parsed) ? 0 : parsed, item.maxLevel - 1))
                  setFromLevel(val)
                  if (toLevel <= val) setToLevel(val + 1)
                }}
                inputProps={{ min: 0, max: item.maxLevel - 1 }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size='small'
                type='number'
                label='To Level'
                value={toLevel || ''}
                onFocus={e => e.target.select()}
                onChange={e => {
                  const parsed = e.target.value === '' ? 1 : parseInt(e.target.value, 10)
                  const val = Math.max(1, Math.min(isNaN(parsed) ? 1 : parsed, item.maxLevel))
                  setToLevel(val)
                  if (fromLevel >= val) setFromLevel(val - 1)
                }}
                inputProps={{ min: 1, max: item.maxLevel }}
              />
              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}
              >
                <Typography
                  variant='caption'
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', fontWeight: 500 }}
                >
                  Total Fragments Needed
                </Typography>
                <Typography
                  variant='h4'
                  key={totalFragments}
                  sx={{
                    fontWeight: 700,
                    color: 'customColors.successText',
                    fontVariantNumeric: 'tabular-nums',
                    fontFamily: 'monospace',
                    mt: 0.5,
                    animation: `${pulse} 0.3s ease`
                  }}
                >
                  {totalFragments.toLocaleString()}
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  Level {fromLevel} → {toLevel}
                </Typography>
              </Box>
            </>
          )}
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          sx={{
            p: 2.5,
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            {item?.name} — Fragment Breakdown
          </Typography>
          {item && (
            <TableContainer ref={tableRef} sx={{ maxHeight: 500 }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle}>Lvl</TableCell>
                    <TableCell sx={thStyle}>Fragments</TableCell>
                    <TableCell sx={thStyle}>Cumulative</TableCell>
                    {item.statNames
                      .filter(s => s !== 'Fragments')
                      .map((name, i) => (
                        <TableCell key={i} sx={thStyle}>
                          {name}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {item.levels.map((row, idx) => {
                    const lvl = row[0]
                    const fragIdx = item.statNames.indexOf('Fragments')
                    const frags = row[fragIdx + 1] || 0
                    const cumulative = item.levels.slice(0, idx + 1).reduce((sum, r) => sum + (r[fragIdx + 1] || 0), 0)
                    const inRange = lvl > fromLevel && lvl <= toLevel

                    return (
                      <TableRow
                        key={idx}
                        data-level={lvl}
                        sx={{
                          ...rowSx(idx),
                          ...(inRange ? { backgroundColor: theme => alpha(theme.palette.success.main, 0.06) } : {})
                        }}
                      >
                        <TableCell sx={{ ...dataCell, ...(inRange ? { fontWeight: 700 } : {}) }}>{lvl}</TableCell>
                        <TableCell sx={dataCell}>{frags}</TableCell>
                        <TableCell sx={dataCell}>{cumulative.toLocaleString()}</TableCell>
                        {item.statNames
                          .filter(s => s !== 'Fragments')
                          .map((_, i) => {
                            const val = row[fragIdx + 2 + i]

                            return (
                              <TableCell key={i} sx={dataCell}>
                                {val === null ? (
                                  <Typography variant='caption' color='text.secondary'>
                                    —
                                  </Typography>
                                ) : typeof val === 'number' ? (
                                  `${val}%`
                                ) : (
                                  val
                                )}
                              </TableCell>
                            )
                          })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Grid>
    </Grid>
  )
}

const STORAGE_KEY = 'gardenConfig'

const GardenPage = () => {
  const [tab, setTab] = useState(0)
  const [plannerLevels, setPlannerLevels] = useState(null)
  const [snackOpen, setSnackOpen] = useState({ open: false, type: 'success', time: undefined })

  const snackClose = () => {
    setSnackOpen({ open: undefined, type: snackOpen.type, time: 3000 })
  }

  // Load from localStorage on mount
  useEffect(() => {
    // El enlace compartido gana sobre lo guardado; lo propio sigue en localStorage
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setPlannerLevels(compartido)

      return
    }

    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY))
      if (saved) {
        setPlannerLevels(saved)
      }
    } catch (e) {}
  }, [])

  const handleShare = async () => {
    const enlace = construirEnlace(aEstadoMinimo(plannerLevels))
    if (!enlace) return setSnackOpen({ open: 'Nothing to share yet.', type: 'error', time: 4000 })

    try {
      await navigator.clipboard.writeText(enlace)

      return setSnackOpen({ open: 'Link copied — paste it anywhere.', type: 'success', time: 4000 })
    } catch (e) {
      window.history.replaceState(null, '', enlace)

      return setSnackOpen({ open: 'Link is now in the address bar — copy it from there.', type: 'info', time: 6000 })
    }
  }

  const handleSave = () => {
    if (plannerLevels) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(plannerLevels))
    }
    setSnackOpen({ open: 'Information Saved.', type: 'success', time: 3000 })
  }

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY)
    setPlannerLevels(null)
    setSnackOpen({ open: 'Information Cleared.', type: 'error', time: 3000 })
  }

  return (
    <>
      <Snackbar
        open={snackOpen.open ? true : false}
        autoHideDuration={snackOpen.time ?? 7000}
        onClose={snackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          variant='outlined'
          onClose={snackClose}
          severity={snackOpen.type === 'error' ? 'info' : 'success'}
          icon={<Icon icon={snackOpen.type === 'error' ? 'mdi:information-outline' : 'mdi:check-circle-outline'} />}
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            '& .MuiAlert-icon': { color: 'text.secondary' },
            '& .MuiAlert-message': { fontWeight: 500, fontSize: '0.8125rem' }
          }}
        >
          {snackOpen.open}
        </Alert>
      </Snackbar>

      <Grid container spacing={6}>
        {/* Header */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              p: { xs: 2.5, md: 3.5 },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <CustomHeader icon='garden' title='GARDEN IN THE SKY' />
            <Divider sx={{ my: 2 }} />
            <Typography variant='body2' sx={{ color: 'text.secondary', mb: 0.5, lineHeight: 1.7 }}>
              Manage decorations and statues that provide stat boosts to your army. Each item can be leveled up. Premium
              decorations unlock special battle skills.
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
              Browse items below to see level progression, stat gains, and how to obtain them.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1.5, mt: 3, mb: 1, alignItems: 'center' }}>
              <GreenButton
                variant='contained'
                size='small'
                startIcon={<Icon icon='ic:baseline-save' />}
                onClick={handleSave}
              >
                Save
              </GreenButton>
              <Button variant='outlined' startIcon={<Icon icon='ic:baseline-link' />} onClick={handleShare}>
                SHARE
              </Button>
              <Button
                variant='outlined'
                color='error'
                size='small'
                startIcon={<Icon icon='ic:baseline-delete' />}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Box>
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                textAlign: 'right',
                color: 'text.secondary',
                fontSize: '0.75rem'
              }}
            >
              Created by DvdVgt
            </Typography>
          </Card>
        </Grid>

        {/* Tabs + Content */}
        <Grid size={{ xs: 12 }}>
          <Card
            sx={{
              p: { xs: 2, md: 3 },
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                minHeight: 36,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0'
                },
                '& .MuiTab-root': {
                  minHeight: 36,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  px: 2,
                  transition: 'color 0.2s ease',
                  '&.Mui-selected': {
                    fontWeight: 600
                  }
                }
              }}
            >
              <Tab label='Garden Planner' />
              <Tab label='Fragment Calculator' />
              <Tab label='Decorations' />
              <Tab label='Decoration Sets' />
              <Tab label='Statues' />
              <Tab label='Event Lookup' />
              <Tab label='Stat Filter' />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <GardenPlannerPanel savedLevels={plannerLevels} onLevelsChange={setPlannerLevels} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <FragmentCalculatorPanel />
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <DecorationsPanel />
            </TabPanel>
            <TabPanel value={tab} index={3}>
              <DecorationSetsPanel />
            </TabPanel>
            <TabPanel value={tab} index={4}>
              <StatuesPanel />
            </TabPanel>
            <TabPanel value={tab} index={5}>
              <EventLookupPanel />
            </TabPanel>
            <TabPanel value={tab} index={6}>
              <StatFilterPanel />
            </TabPanel>
          </Card>
        </Grid>
      </Grid>
        <Grid size={{ xs: 12 }} sx={{ pt: 6 }}>
          <TablaReferencia
            titulo='Every garden piece: category, max level and the stats it gives'
            columnas={COLUMNAS_JARDIN}
            filas={FILAS_JARDIN}
            idFila='id'
          />
        </Grid>
    </>
  )
}

GardenPage.noLoadingGate = true

export default GardenPage
